import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { Stack, Button, Typography, MenuItem } from "@mui/material";
import { BsCloudArrowUp, BsX } from "react-icons/bs";
import CustomTextField from "../../component/CustomTextField";
import ProgressDialog from "../../component/ProgressDialog";
import { Toast } from "../../component/Toast";
import { isEmpty } from "../../services/validation";
import { strings, paths } from "../../utils/constants";
import {
  requestVideoUploadUrlAction,
  uploadVideoToS3Action,
  createAdminVideoAction,
  updateAdminVideoAction,
  requestVideoReplaceUrlAction,
  completeVideoReplacementAction,
  requestVideoReplaceUrlFrAction,
  completeVideoReplacementFrAction,
  fetchVideoPlayUrlAction,
} from "../../redux/Actions";
import {
  MAX_DURATION_SECONDS,
  ACCEPTED_MIME,
  ACCEPTED_EXTENSION,
  isMp4,
  readVideoDuration,
  formatFileSize,
  formatDuration,
} from "../../component/VideoManagement/videoHelpers";
import "../../component/VideoManagement/VideoManagement.css";
import "../../component/FormManagementTable/FormManagementTable.css";

/**
 * The error path in the API layer returns { success: false, message }, so a
 * payload that exists and is not explicitly `success === false` is a success.
 */
const isSaveSuccess = (payload) => !!payload && payload.success !== false;

/**
 * Add / Edit Video page — English + French, shown as two independent columns.
 *
 * A single video record holds BOTH an English and a French video file.
 *
 * - CREATE: title/status + both video columns are submitted together (the record
 *   is created once with the English file required and the French file optional).
 * - EDIT: each column saves INDEPENDENTLY — the English column's "Update"
 *   replaces only the English video, the French column's "Update" replaces only
 *   the French video (and also ADDS French to an English-only record). Title and
 *   status are saved separately via "Save Details".
 */
export default function AddEditVideoPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const enInputRef = useRef(null);
  const frInputRef = useRef(null);
  const enObjectUrlRef = useRef(null);
  const frObjectUrlRef = useRef(null);

  const { isEditVideo, editVideoData } = useSelector(
    (state) => state.adminVideoReducer
  );

  const [title, setTitle] = useState(
    isEditVideo ? editVideoData?.title || "" : ""
  );
  const [titleFr, setTitleFr] = useState(
    isEditVideo ? editVideoData?.titleFr || "" : ""
  );
  const [status, setStatus] = useState(
    isEditVideo ? editVideoData?.status || "active" : "active"
  );

  // Per-language newly-selected files.
  const [enFile, setEnFile] = useState(null);
  const [enPreview, setEnPreview] = useState("");
  const [enDuration, setEnDuration] = useState(null);

  const [frFile, setFrFile] = useState(null);
  const [frPreview, setFrPreview] = useState("");
  const [frDuration, setFrDuration] = useState(null);

  // Separate upload progress per language.
  const [enProgress, setEnProgress] = useState(0);
  const [frProgress, setFrProgress] = useState(0);
  // What's currently saving: "en", "fr", "details", "create", or null.
  const [busy, setBusy] = useState(null);

  const setProgressFor = (lang, value) =>
    lang === "fr" ? setFrProgress(value) : setEnProgress(value);
  const isBusy = busy !== null;

  // Playback URLs of the ALREADY-uploaded videos (edit mode).
  const [existingEnUrl, setExistingEnUrl] = useState("");
  const [existingFrUrl, setExistingFrUrl] = useState("");
  const [existingEnLoading, setExistingEnLoading] = useState(false);
  const [existingFrLoading, setExistingFrLoading] = useState(false);

  const hasFrVideo = !!(
    editVideoData?.hasFrVideo ||
    editVideoData?.fileNameFr ||
    editVideoData?.file_name_fr
  );

  useEffect(() => {
    return () => {
      revokeObjectUrl(enObjectUrlRef);
      revokeObjectUrl(frObjectUrlRef);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load the existing English preview URL in edit mode.
  useEffect(() => {
    let cancelled = false;
    const id = editVideoData?.id;
    if (!isEditVideo || !id) return undefined;

    setExistingEnLoading(true);
    dispatch(fetchVideoPlayUrlAction({ id, videoLang: strings.language.EN }))
      .then((res) => {
        if (!cancelled) setExistingEnUrl(res?.payload?.playUrl || "");
      })
      .catch(() => {
        if (!cancelled) setExistingEnUrl("");
      })
      .finally(() => {
        if (!cancelled) setExistingEnLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditVideo, editVideoData?.id]);

  // Load the existing French preview URL in edit mode (only if a FR file exists).
  useEffect(() => {
    let cancelled = false;
    const id = editVideoData?.id;
    if (!isEditVideo || !id || !hasFrVideo) return undefined;

    setExistingFrLoading(true);
    dispatch(fetchVideoPlayUrlAction({ id, videoLang: strings.language.FR }))
      .then((res) => {
        if (!cancelled) setExistingFrUrl(res?.payload?.playUrl || "");
      })
      .catch(() => {
        if (!cancelled) setExistingFrUrl("");
      })
      .finally(() => {
        if (!cancelled) setExistingFrLoading(false);
      });

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isEditVideo, editVideoData?.id, hasFrVideo]);

  const revokeObjectUrl = (ref) => {
    if (ref.current) {
      URL.revokeObjectURL(ref.current);
      ref.current = null;
    }
  };

  const resetFileState = (lang) => {
    if (lang === "fr") {
      revokeObjectUrl(frObjectUrlRef);
      setFrFile(null);
      setFrPreview("");
      setFrDuration(null);
      if (frInputRef.current) frInputRef.current.value = "";
    } else {
      revokeObjectUrl(enObjectUrlRef);
      setEnFile(null);
      setEnPreview("");
      setEnDuration(null);
      if (enInputRef.current) enInputRef.current.value = "";
    }
    setProgressFor(lang, 0);
  };

  // Shared file validation + preview for either language.
  const handleFileSelected = async (lang, e) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    if (!isMp4(selected)) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: strings.admin.error.VIDEO_FORMAT_INVALID,
      });
      resetFileState(lang);
      return;
    }

    const ref = lang === "fr" ? frObjectUrlRef : enObjectUrlRef;
    revokeObjectUrl(ref);
    const url = URL.createObjectURL(selected);
    ref.current = url;

    try {
      const secs = await readVideoDuration(url);
      if (secs > MAX_DURATION_SECONDS) {
        Toast({
          type: "error",
          title: strings.words.ERROR,
          message: strings.admin.error.VIDEO_DURATION_EXCEEDED,
        });
        resetFileState(lang);
        return;
      }
      if (lang === "fr") {
        setFrFile(selected);
        setFrPreview(url);
        setFrDuration(secs);
      } else {
        setEnFile(selected);
        setEnPreview(url);
        setEnDuration(secs);
      }
      setProgressFor(lang, 0);
    } catch (err) {
      Toast({
        type: "error",
        title: strings.words.ERROR,
        message: strings.admin.error.VIDEO_METADATA_FAILED,
      });
      resetFileState(lang);
    }
  };

  const goToList = () => {
    navigate(paths.adminRoutes.VIDEOS, { replace: true });
  };

  /**
   * Upload a single file to S3 via a pre-signed URL and return { ok, s3Key }.
   */
  const uploadFile = async (file, kind, id, lang) => {
    let urlRes;
    const body = { fileName: file.name, contentType: ACCEPTED_MIME };
    if (kind === "replace") {
      urlRes = await dispatch(requestVideoReplaceUrlAction({ id, body }));
    } else if (kind === "replace-fr") {
      urlRes = await dispatch(requestVideoReplaceUrlFrAction({ id, body }));
    } else {
      urlRes = await dispatch(requestVideoUploadUrlAction(body));
    }

    const payload = urlRes.payload;
    if (!payload?.uploadUrl || !payload?.s3Key) {
      return { ok: false };
    }

    const uploadRes = await dispatch(
      uploadVideoToS3Action({
        uploadUrl: payload.uploadUrl,
        file,
        onProgress: (p) => setProgressFor(lang, p),
      })
    );
    if (!uploadRes.payload?.success) {
      return { ok: false };
    }
    return { ok: true, s3Key: payload.s3Key };
  };

  const toastError = (message) =>
    Toast({ type: "error", title: strings.words.ERROR, message });

  // ── CREATE (single submit: title/status + both files) ───
  const submitCreate = async () => {
    if (isEmpty(title)) return toastError(strings.admin.error.VIDEO_TITLE_REQUIRED);
    if (isEmpty(titleFr))
      return toastError(strings.admin.error.VIDEO_TITLE_FR_REQUIRED);
    if (!enFile) return toastError(strings.admin.error.VIDEO_FILE_REQUIRED);

    setBusy("create");
    setEnProgress(0);
    setFrProgress(0);
    try {
      // Upload the English file and (if present) the French file IN PARALLEL,
      // so both per-column progress bars move at the same time.
      const [en, fr] = await Promise.all([
        uploadFile(enFile, "upload", undefined, "en"),
        frFile
          ? uploadFile(frFile, "upload", undefined, "fr")
          : Promise.resolve({ ok: true, skipped: true }),
      ]);

      if (!en.ok || !fr.ok) {
        toastError(strings.admin.error.VIDEO_UPLOAD_URL_FAILED);
        setBusy(null);
        return;
      }

      let frBlock = {};
      if (frFile && !fr.skipped) {
        frBlock = {
          fileNameFr: frFile.name,
          s3KeyFr: fr.s3Key,
          contentTypeFr: ACCEPTED_MIME,
          fileSizeFr: frFile.size,
          durationFr: Math.round(frDuration),
        };
      }

      const res = await dispatch(
        createAdminVideoAction({
          title: title.trim(),
          titleFr: titleFr.trim(),
          fileName: enFile.name,
          s3Key: en.s3Key,
          contentType: ACCEPTED_MIME,
          fileSize: enFile.size,
          duration: Math.round(enDuration),
          ...frBlock,
        })
      );

      if (isSaveSuccess(res.payload)) {
        Toast({
          type: "success",
          title: strings.words.SUCCESS,
          message: res.payload?.message || "Video uploaded successfully.",
        });
        goToList();
      } else {
        toastError(
          res.payload?.message || strings.admin.error.VIDEO_METADATA_SAVE_FAILED
        );
        setBusy(null);
      }
    } catch (err) {
      toastError(strings.admin.error.VIDEO_UPLOAD_FAILED);
      setBusy(null);
    }
  };

  // ── EDIT: save title/titleFr/status only ────────────────
  const saveDetails = async () => {
    if (isEmpty(title)) return toastError(strings.admin.error.VIDEO_TITLE_REQUIRED);

    setBusy("details");
    try {
      const res = await dispatch(
        updateAdminVideoAction({
          id: editVideoData?.id,
          body: { title: title.trim(), titleFr: titleFr.trim(), status },
        })
      );
      if (isSaveSuccess(res.payload)) {
        Toast({
          type: "success",
          title: strings.words.SUCCESS,
          message: res.payload?.message || "Details saved.",
        });
      } else {
        toastError(
          res.payload?.message || strings.admin.error.VIDEO_METADATA_SAVE_FAILED
        );
      }
    } catch (err) {
      toastError(strings.admin.error.VIDEO_METADATA_SAVE_FAILED);
    } finally {
      setBusy(null);
    }
  };

  // ── EDIT: replace a single language's video independently ─
  const replaceLang = async (lang) => {
    const isFr = lang === "fr";
    const file = isFr ? frFile : enFile;
    const dur = isFr ? frDuration : enDuration;
    if (!file) return toastError(strings.admin.error.VIDEO_FILE_REQUIRED);

    const id = editVideoData?.id;
    setBusy(lang);
    setProgressFor(lang, 0);
    try {
      const uploaded = await uploadFile(
        file,
        isFr ? "replace-fr" : "replace",
        id,
        lang
      );
      if (!uploaded.ok) {
        toastError(strings.admin.error.VIDEO_UPLOAD_URL_FAILED);
        setBusy(null);
        return;
      }

      const body = {
        fileName: file.name,
        s3Key: uploaded.s3Key,
        contentType: ACCEPTED_MIME,
        fileSize: file.size,
        duration: Math.round(dur),
      };
      const res = await dispatch(
        isFr
          ? completeVideoReplacementFrAction({ id, body })
          : completeVideoReplacementAction({ id, body })
      );

      if (isSaveSuccess(res.payload)) {
        Toast({
          type: "success",
          title: strings.words.SUCCESS,
          message:
            res.payload?.message ||
            (isFr ? "French video updated." : "English video updated."),
        });
        goToList();
      } else {
        toastError(
          res.payload?.message || strings.admin.error.VIDEO_METADATA_SAVE_FAILED
        );
        setBusy(null);
      }
    } catch (err) {
      toastError(strings.admin.error.VIDEO_UPLOAD_FAILED);
      setBusy(null);
    }
  };

  // ── One language column (stable structure — never jumps on file pick) ──
  const renderColumn = (lang) => {
    const isFr = lang === "fr";
    const inputRef = isFr ? frInputRef : enInputRef;
    const file = isFr ? frFile : enFile;
    const preview = isFr ? frPreview : enPreview;
    const dur = isFr ? frDuration : enDuration;
    const existingUrl = isFr ? existingFrUrl : existingEnUrl;
    const existingLoading = isFr ? existingFrLoading : existingEnLoading;
    const existingName = isFr
      ? editVideoData?.fileNameFr || editVideoData?.file_name_fr
      : editVideoData?.fileName || editVideoData?.file_name;
    const hasExisting = isFr ? hasFrVideo : isEditVideo;
    const columnBusy = busy === lang;
    const colProgress = isFr ? frProgress : enProgress;
    const isInactive =
      (editVideoData?.status || "").toLowerCase() === "inactive";

    const heading = isFr
      ? strings.video.FRENCH_VIDEO
      : strings.video.ENGLISH_VIDEO;

    // In create mode with no file chosen yet, the empty video box itself is the
    // clickable picker, so the separate compact button is hidden to avoid a
    // redundant control.
    const isCreatePicker = !isEditVideo && !preview;

    // Decide what the (fixed-size) video area shows.
    let videoArea;
    if (preview) {
      // A newly selected file: show its preview with a clear button.
      videoArea = (
        <div className="vm-video-frame">
          {!isBusy && (
            <button
              type="button"
              className="vm-preview-clear"
              title={strings.words.CANCEL}
              onClick={() => resetFileState(lang)}
            >
              <BsX />
            </button>
          )}
          <video className="vm-preview-video" src={preview} controls />
        </div>
      );
    } else if (isEditVideo && !hasExisting) {
      videoArea = (
        <div className="vm-video-frame vm-video-frame--empty">
          <span>{isFr ? strings.video.NO_FR_VIDEO_YET : strings.video.NO_VIDEOS}</span>
        </div>
      );
    } else if (isEditVideo && existingLoading) {
      videoArea = (
        <div className="vm-video-frame vm-video-frame--empty">
          <span>{strings.video.LOADING_VIDEOS}</span>
        </div>
      );
    } else if (isEditVideo && existingUrl) {
      videoArea = (
        <div className="vm-video-frame">
          <video className="vm-preview-video" src={existingUrl} controls />
        </div>
      );
    } else if (isEditVideo) {
      videoArea = (
        <div className="vm-video-frame vm-video-frame--empty">
          <span>
            {isInactive
              ? strings.video.PREVIEW_UNAVAILABLE_INACTIVE
              : strings.video.NO_VIDEOS}
          </span>
        </div>
      );
    } else {
      // Create mode, no file chosen yet: clickable empty area.
      videoArea = (
        <div
          className="vm-video-frame vm-video-frame--empty vm-video-frame--pick"
          onClick={() => !isBusy && inputRef.current?.click()}
        >
          <span className="vm-dropzone-icon">
            <BsCloudArrowUp />
          </span>
          <span>
            {isFr
              ? strings.video.SELECT_FRENCH_VIDEO
              : strings.video.SELECT_ENGLISH_VIDEO}
          </span>
        </div>
      );
    }

    return (
      <div className="vm-col">
        <h4 className="vm-lang-title">
          {heading}
          {isFr ? " *" : !isEditVideo ? " *" : ""}
        </h4>

        <input
          ref={inputRef}
          type="file"
          accept={`${ACCEPTED_MIME},${ACCEPTED_EXTENSION}`}
          style={{ display: "none" }}
          onChange={(e) => handleFileSelected(lang, e)}
          disabled={isBusy}
        />

        {/* Current file name — reserved line so the layout never shifts. */}
        <div className="vm-current-video-head">
          <span className="vm-current-video-label">
            {strings.video.CURRENT_FILE}
          </span>
          <span className="vm-current-video-name">
            {file?.name ||
              existingName ||
              (isEditVideo ? strings.video.NO_FILE_SELECTED : "—")}
          </span>
        </div>

        {/* Fixed-size video area (preview / current / placeholder). */}
        {videoArea}

        {/* Compact choose/replace button.
            - Create mode with no file yet: the empty box itself IS the picker,
              so we hide this button to avoid a redundant control.
            - Once a file is chosen (or in edit mode): show a "Change video"
              button so the action is clear and the layout stays stable. */}
        {!(isCreatePicker) && (
          <Button
            variant="outlined"
            size="small"
            className="vm-choose-btn"
            startIcon={<BsCloudArrowUp />}
            disabled={isBusy}
            onClick={() => inputRef.current?.click()}
            sx={{ color: "#184437", borderColor: "#184437", mt: 1 }}
          >
            {file ? strings.video.CHANGE_VIDEO : strings.video.REPLACE_VIDEO}
          </Button>
        )}

        {/* Meta row — reserved height so chips don't shift the layout. */}
        <div className="vm-meta-row">
          {file ? (
            <div className="vm-file-meta">
              <span className="vm-meta-chip">
                <strong>{strings.video.FILE_SIZE}:</strong>{" "}
                {formatFileSize(file.size)}
              </span>
              <span className="vm-meta-chip">
                <strong>{strings.video.DURATION}:</strong> {formatDuration(dur)}
              </span>
            </div>
          ) : (
            <span className="vm-dropzone-hint">
              {strings.video.MAX_DURATION_HINT}
            </span>
          )}
        </div>

        {/* Per-column progress — this language's own upload progress.
            Shown while THIS column's video is uploading (edit replace, or during
            create when a file for this language is present). */}
        {file && (columnBusy || (busy === "create" && !isEditVideo)) && (
          <>
            <div className="vm-progress-track">
              <div
                className="vm-progress-fill"
                style={{ width: `${colProgress}%` }}
              />
            </div>
            <div className="vm-progress-label">
              {strings.video.UPLOADING} {colProgress}%
            </div>
          </>
        )}

        {/* Per-column Update — EDIT mode only (A), pinned to the bottom. */}
        {isEditVideo && (
          <Stack direction="row" spacing={1} className="vm-col-actions">
            <Button
              variant="contained"
              style={{ backgroundColor: "#184437" }}
              disabled={isBusy || !file}
              onClick={() => replaceLang(lang)}
            >
              {strings.words.UPDATE}
            </Button>
            {file && (
              <Button
                variant="outlined"
                sx={{ color: "#184437", borderColor: "#184437" }}
                disabled={isBusy}
                onClick={() => resetFileState(lang)}
              >
                {strings.words.CANCEL}
              </Button>
            )}
          </Stack>
        )}
      </div>
    );
  };

  return (
    <>
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={2}
        mt={1}
      >
        <Typography variant="h5" sx={{ marginLeft: 5 }}>
          {isEditVideo ? strings.video.EDIT_VIDEO : strings.video.UPLOAD_VIDEO}
        </Typography>
      </Stack>

      <form
        style={{ marginBottom: "5px", marginLeft: "50px", marginRight: "50px" }}
      >
        {/* ── Details: titles + status ── */}
        <CustomTextField
          style={{ width: "100%", marginTop: "5px", marginBottom: "5px" }}
          type="text"
          label={`${strings.video.VIDEO_TITLE_EN}*`}
          variant="outlined"
          focusColor="#184437"
          value={title}
          autoFocus
          autoComplete="off"
          inputProps={{ maxLength: 150 }}
          disabled={isBusy}
          onChange={(e) => setTitle(e.target.value)}
        />
        <CustomTextField
          style={{ width: "100%", marginTop: "12px", marginBottom: "5px" }}
          type="text"
          label={`${strings.video.VIDEO_TITLE_FR}${isEditVideo ? "" : "*"}`}
          variant="outlined"
          focusColor="#184437"
          value={titleFr}
          autoComplete="off"
          inputProps={{ maxLength: 150 }}
          disabled={isBusy}
          onChange={(e) => setTitleFr(e.target.value)}
        />
        <CustomTextField
          select
          style={{ width: "100%", marginTop: "10px", marginBottom: "5px" }}
          label={strings.video.STATUS}
          variant="outlined"
          focusColor="#184437"
          value={status}
          disabled={isBusy}
          onChange={(e) => setStatus(e.target.value)}
        >
          <MenuItem value="active">{strings.video.ACTIVE}</MenuItem>
          <MenuItem value="inactive">{strings.video.INACTIVE}</MenuItem>
        </CustomTextField>

        {/* ── Two columns: English | French ── */}
        <div className="vm-two-col">
          {renderColumn("en")}
          {renderColumn("fr")}
        </div>

        {/* EDIT mode: Save Details + Cancel at the bottom. */}
        {isEditVideo && (
          <Stack
            direction="row"
            spacing={1}
            justifyContent="flex-end"
            sx={{ mt: 2 }}
          >
            <Button
              variant="outlined"
              sx={{ color: "#184437", borderColor: "#184437" }}
              disabled={isBusy}
              onClick={goToList}
            >
              {strings.words.CANCEL}
            </Button>
            <Button
              variant="contained"
              style={{ backgroundColor: "#184437" }}
              disabled={isBusy}
              onClick={saveDetails}
            >
              {strings.video.SAVE_DETAILS}
            </Button>
          </Stack>
        )}

        {/* CREATE mode: single submit for everything (per-column progress is
            shown inside each column during upload). */}
        {!isEditVideo && (
          <>
            <Stack
              direction={{ xs: "column", sm: "row" }}
              alignItems="start"
              justifyContent="left"
            >
              <Button
                variant="contained"
                style={{
                  backgroundColor: "#184437",
                  marginTop: "14px",
                  marginRight: "12px",
                }}
                disabled={isBusy}
                onClick={submitCreate}
              >
                {strings.video.UPLOAD}
              </Button>
              <Button
                variant="contained"
                style={{ backgroundColor: "#184437", marginTop: "14px" }}
                disabled={isBusy}
                onClick={goToList}
              >
                {strings.words.CANCEL}
              </Button>
            </Stack>
          </>
        )}
      </form>

      {/* Metadata-only save (details, no file upload) shows the overlay. */}
      {busy === "details" && <ProgressDialog open={true} isAdmin={true} />}
    </>
  );
}
